import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Mail, Phone } from 'lucide-react';
import Navbar from '@/components/Navbar';

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
                  <p className="text-zinc-500">Last updated: December 2025</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="prose prose-invert max-w-none">
              <div className="glass-card rounded-xl p-8 space-y-8">
                
                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    By accessing or using Blackified ("the Platform"), you agree to be bound by these Terms of Service 
                    ("Terms"). If you do not agree to all of these Terms, you may not access or use the Platform.
                  </p>
                  <p className="text-zinc-400 leading-relaxed mt-3">
                    We reserve the right to modify these Terms at any time. Your continued use of the Platform after 
                    any changes constitutes acceptance of those changes.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">2. Description of Service</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    Blackified is an online directory and community platform for Black-owned businesses. Our services include:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
                    <li>Business listing and discovery</li>
                    <li>Business verification services</li>
                    <li>Melanin (MEL) rewards system</li>
                    <li>Reviews and ratings</li>
                    <li>Bounty system for community contributions</li>
                    <li>Virtual accounts (Melanin Account) for fiat-to-token conversion</li>
                    <li>Cryptocurrency wallet integration</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">3. User Accounts</h2>
                  
                  <h3 className="text-lg font-medium text-white mt-6 mb-3">3.1 Registration</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    To access certain features, you must create an account. You agree to provide accurate, current, 
                    and complete information during registration and to update such information to keep it accurate.
                  </p>

                  <h3 className="text-lg font-medium text-white mt-6 mb-3">3.2 Account Security</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    You are responsible for safeguarding your account credentials and for all activities that occur 
                    under your account. You must notify us immediately of any unauthorized access or use.
                  </p>

                  <h3 className="text-lg font-medium text-white mt-6 mb-3">3.3 Account Termination</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    We reserve the right to suspend or terminate accounts that violate these Terms or for any other 
                    reason at our sole discretion.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">4. Business Listings</h2>
                  
                  <h3 className="text-lg font-medium text-white mt-6 mb-3">4.1 Eligibility</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    Blackified is a directory for Black-owned businesses. By submitting a listing, you represent and 
                    warrant that the business is at least 51% Black-owned or is a sole proprietorship owned by a 
                    Black individual.
                  </p>

                  <h3 className="text-lg font-medium text-white mt-6 mb-3">4.2 Accuracy</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    You are responsible for ensuring all information in your business listing is accurate, complete, 
                    and up-to-date. Misleading or false information may result in listing removal and account suspension.
                  </p>

                  <h3 className="text-lg font-medium text-white mt-6 mb-3">4.3 Verification</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    We reserve the right to verify business ownership and Black-owned status. You agree to cooperate 
                    with any verification requests and provide supporting documentation when requested.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">5. Melanin Rewards & Tokens</h2>
                  
                  <h3 className="text-lg font-medium text-white mt-6 mb-3">5.1 Melanin (MEL) Points</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    MEL is a rewards currency within the Blackified platform. MEL can be earned through various 
                    activities including business submissions, reviews, referrals, and bounty completion. MEL can 
                    be spent on boosts, badges, and other platform features.
                  </p>

                  <h3 className="text-lg font-medium text-white mt-6 mb-3">5.2 MELANIN Tokens</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    MEL points can be converted to MELANIN cryptocurrency tokens on the Ethereum blockchain. 
                    Cryptocurrency transactions are subject to network fees and blockchain conditions. We are 
                    not responsible for any losses due to blockchain issues or user error.
                  </p>

                  <h3 className="text-lg font-medium text-white mt-6 mb-3">5.3 Melanin Account</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    The Melanin Account feature allows users to hold USD balances and convert between USD and MEL. 
                    This feature is powered by third-party providers and subject to identity verification (KYC) 
                    requirements. Use of this feature is subject to applicable financial regulations.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">6. User Conduct</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    You agree not to:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
                    <li>Submit false or misleading business information</li>
                    <li>Impersonate others or misrepresent your affiliation with any business</li>
                    <li>Post fake reviews or manipulate ratings</li>
                    <li>Use the Platform for any illegal purpose</li>
                    <li>Harass, abuse, or threaten other users</li>
                    <li>Attempt to gain unauthorized access to the Platform</li>
                    <li>Interfere with the proper functioning of the Platform</li>
                    <li>Use bots or automated systems to interact with the Platform</li>
                    <li>Engage in fraud or manipulation of the rewards system</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">7. Intellectual Property</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    The Platform and its original content, features, and functionality are owned by Blackified and 
                    are protected by copyright, trademark, and other intellectual property laws. You may not copy, 
                    modify, distribute, or reverse engineer any part of the Platform without our express written consent.
                  </p>
                  <p className="text-zinc-400 leading-relaxed mt-3">
                    By submitting content to the Platform (including business listings, reviews, and images), you 
                    grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute that 
                    content in connection with our services.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">8. Disclaimers</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                    EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, 
                    OR SECURE.
                  </p>
                  <p className="text-zinc-400 leading-relaxed mt-3">
                    We do not endorse or guarantee the quality, safety, or legality of any businesses listed on 
                    the Platform. Users interact with listed businesses at their own risk.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, BLACKIFIED SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATING TO YOUR 
                    USE OF THE PLATFORM, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL.
                  </p>
                  <p className="text-zinc-400 leading-relaxed mt-3">
                    Our total liability for any claims arising under these Terms shall not exceed the amount 
                    you have paid to us in the past twelve months.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">10. Indemnification</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    You agree to indemnify and hold harmless Blackified, its officers, directors, employees, and 
                    agents from any claims, damages, losses, or expenses (including reasonable attorney's fees) 
                    arising from your use of the Platform or violation of these Terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">11. Dispute Resolution</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    Any disputes arising from these Terms or your use of the Platform shall be resolved through 
                    binding arbitration in accordance with the rules of the American Arbitration Association. 
                    The arbitration shall take place in Cleveland, Ohio. You waive any right to participate in 
                    a class action lawsuit.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">12. Governing Law</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    These Terms shall be governed by and construed in accordance with the laws of the State of Ohio, 
                    without regard to its conflict of law principles.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">13. Severability</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    If any provision of these Terms is found to be unenforceable or invalid, that provision shall 
                    be limited or eliminated to the minimum extent necessary, and the remaining provisions shall 
                    remain in full force and effect.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">14. Contact Us</h2>
                  <p className="text-zinc-400 leading-relaxed mb-4">
                    If you have questions about these Terms, please contact us:
                  </p>
                  <div className="flex flex-col gap-3">
                    <a href="mailto:legal@blackified.com" className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
                      <Mail className="w-4 h-4" />
                      legal@blackified.com
                    </a>
                    <a href="tel:+12166267560" className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
                      <Phone className="w-4 h-4" />
                      (216) 626-7560
                    </a>
                  </div>
                </section>

              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">
            © 2025 Blackified. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-zinc-400 hover:text-white text-sm">
              Privacy Policy
            </Link>
            <Link to="/help" className="text-zinc-400 hover:text-white text-sm">
              Help Center
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
